'use client'

import { useCallback, useMemo, useState } from 'react'
import Link from 'next/link'
import { Sparkles, Shuffle, RotateCcw, Eye, Wand2 } from 'lucide-react'

/**
 * Interactive 3-card tarot reading (Past · Present · Future).
 *
 * Fully client-side, no backend — works with `output: 'export'`:
 *  1. idle      — a stacked deck floats, "Shuffle & Begin" CTA.
 *  2. shuffling — the stack fans out and snaps back (~1.3s), then deals
 *                 the deck into a fanned spread.
 *  3. picking   — the 22 Major Arcana lie face-down in an arc; the user
 *                 taps 3, which fly up into the Past/Present/Future slots.
 *  4. reading   — each slotted card flips (3D rotateY) to reveal its face
 *                 and interpretation; a short disclaimer + app CTA follow.
 *
 * Card orientation (upright / reversed) is randomised per draw on click —
 * this is user-triggered *after* mount, so Math.random() introduces no
 * hydration mismatch. Animations live in globals.css for the same reason.
 */

type Card = {
  num: string
  name: string
  emoji: string
  keywords: string
  upright: string
  reversed: string
}

// The 22 Major Arcana — the heart of any tarot reading.
// The card face image lives at /images/tarot/<name>.png (the uploaded
// filenames match each card's display name exactly).
const DECK: Card[] = [
  { num: '0', name: 'The Fool', emoji: '🌈', keywords: 'New beginnings, spontaneity, a leap of faith', upright: 'You are standing at the start of something new. This is the time to take a chance and trust yourself, even if you cannot see the whole path yet. Let go of the fear of looking foolish and follow what genuinely excites you. Take that first step with an open, curious mind. Good things come to you when you are brave enough to begin.', reversed: 'Something is holding you back — either fear that keeps you stuck, or rushing in without thinking. You may be ignoring real risks or acting carelessly. Slow down and look honestly at what could go wrong before you leap. Make a simple plan so you feel more secure. Once you balance courage with common sense, you can move ahead safely.' },
  { num: 'I', name: 'The Magician', emoji: '🪄', keywords: 'Manifestation, willpower, resourcefulness', upright: 'You already have everything you need to make your goal happen. Your skills, focus, and willpower are strong right now, so use them with purpose. Decide clearly what you want and take practical action toward it. This is a powerful time to turn your ideas into real results. Believe in your ability to create the outcome you want.', reversed: 'Your energy feels scattered and your talents are not being used well. You may be doubting yourself or getting pulled in too many directions. Stop and reconnect with your real goal. Cut out what is wasting your time and focus on one clear step at a time. Once your actions match your intention, your power returns.' },
  { num: 'II', name: 'The High Priestess', emoji: '🌙', keywords: 'Intuition, mystery, the inner voice', upright: 'The answer you are looking for is inside you, not in the noise around you. Trust your gut feeling and pay attention to your quiet thoughts and dreams. This is a time to listen and observe more than to act. Give yourself space to reflect before you make any decision. Your intuition is guiding you well right now.', reversed: 'You are ignoring your inner voice or letting other people drown it out. Deep down you already sense the truth, but you keep second-guessing it. Step away from the outside opinions for a while. Get quiet and be honest with yourself about what you truly feel. Trust that inner knowing again before you decide anything.' },
  { num: 'III', name: 'The Empress', emoji: '🌷', keywords: 'Abundance, nurturing, creativity', upright: 'This is a warm and fruitful time full of growth and comfort. Something you care about is ready to bloom — a project, a relationship, or your own well-being. Nurture it with love and patience and it will flourish. Enjoy the good things around you and let your creativity flow. Abundance is coming, so stay open to receive it.', reversed: 'You may feel creatively stuck or drained from giving too much to others. You have been caring for everyone except yourself. Take a step back and refill your own cup first. Rest, do something that brings you joy, and be gentle with yourself. Once you nurture yourself, your energy and creativity will return.' },
  { num: 'IV', name: 'The Emperor', emoji: '👑', keywords: 'Structure, authority, stability', upright: 'It is time to bring order and structure to your life. Make a clear plan, set firm boundaries, and take charge of your situation. Steady discipline and effort will build a strong foundation for you. Lead with confidence and take responsibility for what is yours. The stability you create now leads to lasting success.', reversed: 'You may be too controlling, or you feel powerless under someone else’s control. Rigid rules and stubbornness are getting in the way. Loosen your grip and stay flexible. Lead with fairness instead of force, and let others have a say. Balancing strength with kindness will fix the situation.' },
  { num: 'V', name: 'The Hierophant', emoji: '🗝️', keywords: 'Tradition, guidance, wisdom', upright: 'Look to trusted knowledge, a mentor, or a proven path for guidance now. There is real value in tradition and doing things the established way. Learn from someone with experience before trying your own approach. Structure and shared beliefs can give you support. Follow the tried-and-true method for the time being.', reversed: 'You are ready to question the rules and think for yourself. What works for other people may simply not fit you. It is okay to break from tradition and find your own way. Trust your personal values over other people’s expectations. Do what feels genuinely right and true to you.' },
  { num: 'VI', name: 'The Lovers', emoji: '💞', keywords: 'Union, choices, values', upright: 'A meaningful connection or an important choice is in front of you. In relationships, this points to harmony, love, and deep understanding. In decisions, it asks you to choose based on your true values. Be honest with yourself about what matters most. When you follow your heart, the right path becomes clear.', reversed: 'There is tension, disagreement, or a mismatch in values right now. A relationship may feel out of balance, or you are stuck on a hard choice. Communicate openly and honestly to clear the air. Check whether your choices actually match your values. Reconnect where you can, or step back so you can realign.' },
  { num: 'VII', name: 'The Chariot', emoji: '🏇', keywords: 'Drive, determination, victory', upright: 'Success comes now through focus, determination, and self-control. You can overcome the obstacles if you stay confident and keep moving. Take charge and steer your own direction firmly. Do not let distractions pull you off course. With steady willpower, the victory is yours.', reversed: 'You feel scattered or like you have lost your direction. Opposing pressures are pulling at you and progress has stalled. Pause and get clear about what you actually want. Gather your focus and control your reactions before pushing ahead. Once you are centered again, you can move forward.' },
  { num: 'VIII', name: 'Strength', emoji: '🦁', keywords: 'Courage, patience, inner power', upright: 'Your real power comes from calm, patience, and inner courage. You can handle this challenge with a steady heart instead of force. Stay gentle but firm, and trust your own resilience. Compassion and self-control will carry you through. Believe in your quiet strength.', reversed: 'You may feel low on confidence, drained, or overwhelmed. Self-doubt or frustration is slowly wearing you down. Be kinder to yourself and rest when you need to. Rebuild your inner strength one small step at a time. Patience with yourself will bring your courage back.' },
  { num: 'IX', name: 'The Hermit', emoji: '🏮', keywords: 'Reflection, solitude, inner search', upright: 'This is a time to step back and look within for answers. Take some quiet time alone to think clearly, away from the noise. Honest reflection will show you what you truly need. Trust your own inner wisdom to light the way. The clarity you are seeking comes from within.', reversed: 'You may be isolating yourself too much or avoiding the world. Too much time alone has started to feel lonely rather than helpful. It is time to reconnect with people and rejoin life. Balance your need for reflection with real connection. Do not hide away from the support around you.' },
  { num: 'X', name: 'Wheel of Fortune', emoji: '🎡', keywords: 'Cycles, luck, turning points', upright: 'Change is here, and luck is turning in your favour. A new cycle is beginning and bringing fresh opportunities. Stay flexible and go with the flow of events. What felt stuck is finally starting to move. Trust the timing and welcome the good change.', reversed: 'You may be hitting a run of bad luck or frustrating delays. Things feel like they are working against you right now. Remember that every cycle passes, so do not lose hope. Focus on what you can control and wait patiently. The wheel will turn back in your favour soon.' },
  { num: 'XI', name: 'Justice', emoji: '⚖️', keywords: 'Fairness, truth, cause & effect', upright: 'Truth, fairness, and balance are at the centre of your situation. What you put out will come back to you, so act with honesty. A fair outcome or decision is on its way. Take responsibility for your choices. Do the right thing and trust that it will be rewarded.', reversed: 'Something feels unfair, or you are avoiding responsibility. You may be dodging the truth or facing an unjust situation. Be honest about your own part in what happened. Face the facts clearly and look for a fair solution. Accountability is the way to set things right.' },
  { num: 'XII', name: 'The Hanged Man', emoji: '🙃', keywords: 'Surrender, new perspective, pause', upright: 'Now is the time to pause instead of pushing forward. Let go of control and look at your situation from a new angle. A change in perspective will reveal something you have been missing. Surrender to the waiting period and be patient. Sometimes doing nothing is the wisest move.', reversed: 'You are stuck, stalling, or holding on to something that no longer helps you. You may be delaying a needed change or resisting a fresh point of view. Let go of whatever is keeping you frozen. Stop putting off the decision and start moving. Release the old to make room for the new.' },
  { num: 'XIII', name: 'Death', emoji: '🦋', keywords: 'Transformation, endings, rebirth', upright: 'An ending is happening to make space for a new beginning. Something in your life is finished, and that is okay. Let it go so a real transformation can take place. This change may be hard, but it is for your growth. Welcome the fresh start that follows.', reversed: 'You are resisting a change that truly needs to happen. Holding on to the past is keeping you stuck and prolonging the discomfort. Ask yourself what you are so afraid to release. Accept that this ending is necessary for you. Once you let go, you can finally move on.' },
  { num: 'XIV', name: 'Temperance', emoji: '⚗️', keywords: 'Balance, moderation, patience', upright: 'Balance, patience, and moderation bring peace to your life now. Blend the different parts of your life calmly instead of going to extremes. Take the middle path and avoid rushing things. Steady, patient effort creates real harmony. Keep everything measured and calm.', reversed: 'Something in your life is out of balance or has gone to excess. You may be overdoing one area while neglecting another. Slow down and find the middle ground again. Bring your priorities back into harmony. Moderation will restore your sense of calm.' },
  { num: 'XV', name: 'The Devil', emoji: '😈', keywords: 'Attachment, temptation, shadow', upright: 'Notice what is holding you back — a bad habit, a fear, or an unhealthy attachment. You may feel trapped, but often the chains are looser than they seem. Be honest about what has power over you. Awareness is the first real step to freedom. You can choose to break free.', reversed: 'You are starting to break free from what once controlled you. A harmful habit, fear, or relationship is losing its grip. Keep going, because you are reclaiming your own power. Face the shadow honestly and release it for good. Freedom is within your reach.' },
  { num: 'XVI', name: 'The Tower', emoji: '🗼', keywords: 'Upheaval, revelation, awakening', upright: 'A sudden change is clearing away what was not built on truth. It may feel shocking, but it is removing something unstable from your life. Let the false foundations fall away. This upheaval makes room for something stronger and real. Trust that this break needed to happen.', reversed: 'You sense a big change coming and you are trying to avoid it. Delaying the inevitable only makes it harder later. Face the truth on your own terms before it forces itself. Deal with the shaky part of your life now. Handling it early makes the change much easier.' },
  { num: 'XVII', name: 'The Star', emoji: '⭐', keywords: 'Hope, healing, renewal', upright: 'Hope and healing are returning to your life. After a hard time, you now feel calmer, renewed, and hopeful again. Trust that things are genuinely getting better. Follow what inspires you and keep the faith. The universe is gently guiding you forward.', reversed: 'Your hope or faith may feel shaken right now. You could be feeling discouraged or cut off from what inspires you. Take time to heal and reconnect with your hopes. Remember your strengths and what you are working toward. Let hope back in, one small step at a time.' },
  { num: 'XVIII', name: 'The Moon', emoji: '🌕', keywords: 'Illusion, intuition, the unseen', upright: 'Things are not fully clear right now, so be careful of illusions. Your fears or imagination may be distorting how you see the situation. Trust your intuition over surface appearances. Avoid making big decisions until more is revealed. Let the truth come to light in its own time.', reversed: 'Confusion is lifting and clarity is returning to you. The fear or doubt that clouded your view is fading away. You are beginning to see the truth clearly. Trust the understanding that is now coming to you. The path ahead is getting easier to see.' },
  { num: 'XIX', name: 'The Sun', emoji: '☀️', keywords: 'Joy, success, vitality', upright: 'This is a bright, happy, and successful time for you. Joy, warmth, and clarity are shining on your life. Enjoy this positive moment and let yourself feel good. Success and confidence are within your reach. Everything is coming together well.', reversed: 'A bit of cloud is temporarily blocking your sunshine. You may feel less optimistic or slightly out of step for now. The good energy is still there, just a little dimmed. Look for the small joys around you each day. Brighter days are just ahead.' },
  { num: 'XX', name: 'Judgement', emoji: '📯', keywords: 'Awakening, reckoning, renewal', upright: 'This is a moment of awakening and honest self-reflection. You are being called to rise up and answer your true purpose. Look back at your past with acceptance and forgive yourself. Make a clear-eyed decision and step into a new chapter. Trust your calling and move forward with a clear conscience.', reversed: 'You may be judging yourself too harshly or hesitating to move on. Guilt about the past is holding you back from your next step. Forgive yourself and let the past go. Listen to your inner call instead of your doubts. Give yourself permission to begin again.' },
  { num: 'XXI', name: 'The World', emoji: '🌍', keywords: 'Completion, wholeness, achievement', upright: 'A major cycle is completing in success, and you should feel proud. You have worked hard and reached a real milestone. Take time to celebrate how far you have come. You feel whole, complete, and accomplished. A wonderful sense of achievement is yours.', reversed: 'You are close to finishing, but not quite there yet. A few loose ends still need to be tied up. Do not give up right before the finish line. Complete the final steps and give yourself proper closure. The reward is waiting just ahead.' },
]

type Drawn = { card: Card; reversed: boolean }
type Phase = 'idle' | 'shuffling' | 'picking' | 'reading'

// Position labels per spread size. A single-card draw is a quick
// "guidance for now" pull; the three-card draw is Past/Present/Future.
const SPREADS: Record<number, { label: string; hint: string }[]> = {
  1: [{ label: 'Your Card', hint: 'Guidance for right now' }],
  3: [
    { label: 'Past', hint: 'What shaped this moment' },
    { label: 'Present', hint: 'Where you stand now' },
    { label: 'Future', hint: 'Where the path leads' },
  ],
}

// Fisher–Yates shuffle with a random reversed orientation per card.
function shuffleDeck(): Drawn[] {
  const arr = DECK.map(card => ({ card, reversed: Math.random() < 0.5 }))
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

/* ── The decorative back of a card (deck + face-down states) ── */
function CardBack() {
  return (
    <div className="tarot-back">
      <div className="tarot-back-emblem">
        <Sparkles size={22} strokeWidth={1.5} />
      </div>
    </div>
  )
}

/* ── The revealed face of a drawn card ──
   Prefers the real card artwork at /images/tarot/<img>.jpg. If that file
   isn't present (or fails to load), it gracefully falls back to the
   emoji/name design so the feature always works. Drop the 22 Major
   Arcana images into public/images/tarot/ to switch to real art. */
function CardFace({ drawn }: { drawn: Drawn }) {
  const { card, reversed } = drawn
  const [imgOk, setImgOk] = useState(true)

  if (imgOk) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          borderRadius: '14px',
          overflow: 'hidden',
          border: '1px solid var(--border-hover)',
          background: 'var(--bg-card)',
        }}
      >
        {/* A reversed card is rendered upside-down, matching how a real
            tarot reading presents it. */}
        <img
          src={`/images/tarot/${encodeURIComponent(card.name)}.png`}
          alt={`${card.name}${reversed ? ' (reversed)' : ''}`}
          onError={() => setImgOk(false)}
          decoding="async"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            transform: reversed ? 'rotate(180deg)' : 'none',
          }}
        />
        {reversed && (
          <span
            style={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '9px',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#fff',
              background: 'rgba(0,0,0,0.6)',
              padding: '2px 8px',
              borderRadius: '100px',
            }}
          >
            Reversed
          </span>
        )}
      </div>
    )
  }

  // Fallback design (used until the real images are added).
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: 'var(--bg-card)',
        border: '1px solid var(--border-hover)',
        borderRadius: '14px',
        padding: '14px 10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'center',
      }}
    >
      <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.1em' }}>
        {card.num}
      </span>
      {/* The emoji flips 180° when the card is reversed — a subtle visual
          cue that the reading uses the reversed meaning. */}
      <span
        style={{
          fontSize: '40px',
          lineHeight: 1,
          transform: reversed ? 'rotate(180deg)' : 'none',
          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))',
        }}
      >
        {card.emoji}
      </span>
      <span
        style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '14px',
          fontWeight: 800,
          color: 'var(--text-primary)',
          lineHeight: 1.15,
        }}
      >
        {card.name}
      </span>
      <span
        style={{
          fontSize: '10px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: reversed ? 'var(--text-muted)' : 'var(--accent)',
        }}
      >
        {reversed ? 'Reversed' : 'Upright'}
      </span>
    </div>
  )
}

export default function TarotReading() {
  const [phase, setPhase] = useState<Phase>('idle')
  const [count, setCount] = useState<number>(3) // 1 or 3 cards
  const [deck, setDeck] = useState<Drawn[]>([])
  const [picked, setPicked] = useState<number[]>([]) // indices into `deck`
  const [revealed, setRevealed] = useState<boolean[]>([false, false, false])

  const positions = SPREADS[count]

  const startShuffle = useCallback((n: number) => {
    setCount(n)
    setPicked([])
    setRevealed(Array(n).fill(false))
    setDeck(shuffleDeck())
    setPhase('shuffling')
    // Let the shuffle keyframe play, then reveal the fanned spread.
    window.setTimeout(() => setPhase('picking'), 1300)
  }, [])

  const pickCard = useCallback(
    (deckIndex: number) => {
      setPicked(prev => {
        if (prev.includes(deckIndex) || prev.length >= count) return prev
        const next = [...prev, deckIndex]
        if (next.length === count) {
          // Small beat so the last card settles before we switch views.
          window.setTimeout(() => setPhase('reading'), 480)
        }
        return next
      })
    },
    [count],
  )

  const flip = useCallback((slot: number) => {
    setRevealed(prev => {
      if (prev[slot]) return prev
      const next = [...prev]
      next[slot] = true
      return next
    })
  }, [])

  const revealAll = useCallback(() => setRevealed(prev => prev.map(() => true)), [])
  const reset = useCallback(() => {
    setPhase('idle')
    setPicked([])
    setRevealed([false, false, false])
  }, [])

  const allRevealed = revealed.every(Boolean)
  const drawnCards = useMemo(() => picked.map(i => deck[i]), [picked, deck])

  return (
    <div style={{ position: 'relative', zIndex: 1 }}>
      {/* ── Header ── */}
      <section style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)', padding: '60px 24px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 700, letterSpacing: '0.15em', color: 'var(--accent)', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Wand2 size={14} /> Tarot Reading
          </div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 900, color: 'var(--text-primary)', marginBottom: '14px' }}>
            Draw Your Cards
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '18px', maxWidth: '600px', lineHeight: 1.7 }}>
            Take a breath and hold your question in mind. Choose a one-card pull for quick guidance, or a three-card Past · Present · Future spread — then shuffle the Major Arcana and let the cards speak.
          </p>
        </div>
      </section>

      {/* ── Interactive stage ── */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '48px 24px 72px' }}>

        {/* ---------- IDLE: floating deck + CTA ---------- */}
        {phase === 'idle' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '36px', paddingTop: '20px' }}>
            <div style={{ position: 'relative', width: '150px', height: '250px', animation: 'tarotFloat 5s ease-in-out infinite' }}>
              {[0, 1, 2, 3].map(i => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    transform: `translate(${i * 4}px, ${i * -5}px) rotate(${(i - 1.5) * 2}deg)`,
                    boxShadow: '0 12px 34px rgba(0,0,0,0.35)',
                    borderRadius: '14px',
                  }}
                >
                  <CardBack />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button type="button" onClick={() => startShuffle(1)} className="btn-secondary" style={{ fontSize: '16px', padding: '14px 28px' }}>
                <Sparkles size={18} /> One Card
              </button>
              <button type="button" onClick={() => startShuffle(3)} className="btn-primary" style={{ fontSize: '16px', padding: '14px 28px' }}>
                <Shuffle size={18} /> Three Cards
              </button>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center', maxWidth: '420px', lineHeight: 1.6 }}>
              For entertainment and reflection. Focus on one clear question for the most meaningful draw.
            </p>
          </div>
        )}

        {/* ---------- SHUFFLING: the stack fans out & snaps back ---------- */}
        {phase === 'shuffling' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '32px', paddingTop: '20px' }}>
            <div style={{ position: 'relative', width: '150px', height: '250px' }}>
              {[0, 1, 2, 3, 4].map(i => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '14px',
                    boxShadow: '0 12px 34px rgba(0,0,0,0.35)',
                    animation: `tarotShuffle 1.2s ease-in-out ${i * 0.08}s`,
                  }}
                >
                  <CardBack />
                </div>
              ))}
            </div>
            <p style={{ fontSize: '15px', color: 'var(--accent)', fontWeight: 600, letterSpacing: '0.05em' }}>
              Shuffling the cards…
            </p>
          </div>
        )}

        {/* ---------- PICKING: fanned spread + slots ---------- */}
        {phase === 'picking' && (
          <div>
            {/* Chosen-so-far slots */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '10px', flexWrap: 'wrap' }}>
              {positions.map((pos, i) => (
                <Slot key={pos.label} label={pos.label} filled={picked[i] !== undefined} index={i}>
                  {picked[i] !== undefined && <CardBack />}
                </Slot>
              ))}
            </div>
            <p style={{ textAlign: 'center', fontSize: '15px', color: 'var(--text-secondary)', margin: '18px 0 30px', fontWeight: 500 }}>
              Choose <strong style={{ color: 'var(--accent)' }}>{count - picked.length}</strong> more {count - picked.length === 1 ? 'card' : 'cards'} — trust your instinct.
            </p>

            {/* Fanned deck */}
            <div
              role="group"
              aria-label="Face-down tarot cards to choose from"
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'flex-end',
                // Generous top padding leaves room for the hover lift, and
                // the large bottom padding stops the rotated/lowered end
                // cards from being clipped at the container's padding edge.
                padding: '64px 20px 96px',
                minHeight: '300px',
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
            >
              <div style={{ display: 'flex', margin: '0 auto', paddingRight: '40px' }}>
                {deck.map((_, i) => {
                  const isPicked = picked.includes(i)
                  const mid = (deck.length - 1) / 2
                  const rotate = (i - mid) * 2.4
                  const lift = Math.abs(i - mid) * 3
                  return (
                    <button
                      key={i}
                      type="button"
                      className="tarot-pick"
                      disabled={isPicked}
                      onClick={() => pickCard(i)}
                      aria-label={`Pick card ${i + 1}`}
                      style={{
                        flex: '0 0 auto',
                        width: '82px',
                        height: '138px',
                        marginLeft: i === 0 ? 0 : '-42px',
                        padding: 0,
                        border: 'none',
                        background: 'transparent',
                        transform: `rotate(${rotate}deg) translateY(${lift}px)`,
                        transformOrigin: 'bottom center',
                        zIndex: i,
                        opacity: isPicked ? 0 : 1,
                        pointerEvents: isPicked ? 'none' : 'auto',
                        transition: 'opacity 0.4s ease, transform 0.3s ease',
                        borderRadius: '14px',
                        boxShadow: '0 8px 22px rgba(0,0,0,0.3)',
                      }}
                    >
                      <CardBack />
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* ---------- READING: flip the three chosen cards ---------- */}
        {phase === 'reading' && (
          <div>
            {/* One row per card: bigger card on the left, its guidance
                beside it. Rows stack vertically; on narrow screens the
                text wraps below the card. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '660px', margin: '0 auto' }}>
              {drawnCards.map((drawn, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: '24px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    padding: '20px',
                    borderRadius: '16px',
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    animation: 'tarotFadeUp 0.5s ease-out',
                  }}
                >
                  {/* Card (left) */}
                  <button
                    type="button"
                    onClick={() => flip(i)}
                    aria-label={revealed[i] ? `${drawn.card.name}, ${drawn.reversed ? 'reversed' : 'upright'}` : `Reveal your ${positions[i].label} card`}
                    className={`tarot-flip ${revealed[i] ? 'is-flipped' : ''}`}
                    style={{
                      // A single-card draw gets a noticeably larger card
                      // since it has the whole row to itself.
                      width: count === 1 ? '260px' : '184px',
                      height: count === 1 ? '435px' : '308px',
                      flexShrink: 0,
                      padding: 0,
                      border: 'none',
                      background: 'transparent',
                      cursor: revealed[i] ? 'default' : 'pointer',
                      borderRadius: '14px',
                      animation: revealed[i] ? 'none' : 'tarotPulse 2.4s ease-in-out infinite',
                    }}
                  >
                    <div className="tarot-flip-inner">
                      <div className="tarot-face tarot-face-back">
                        <CardBack />
                      </div>
                      <div className="tarot-face tarot-face-front">
                        <CardFace drawn={drawn} />
                      </div>
                    </div>
                  </button>

                  {/* Guidance (right) */}
                  <div style={{ flex: '1 1 240px', minWidth: '220px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '8px' }}>
                      {positions[i].label}
                    </div>

                    {revealed[i] ? (
                      <>
                        <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '23px', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.15, marginBottom: '4px' }}>
                          {drawn.card.name}
                          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginLeft: '8px', verticalAlign: 'middle' }}>
                            {drawn.reversed ? 'Reversed' : 'Upright'}
                          </span>
                        </h3>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px', lineHeight: 1.5 }}>
                          {drawn.card.keywords}
                        </div>
                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.7, animation: 'tarotFadeUp 0.5s ease-out' }}>
                          {drawn.reversed ? drawn.card.reversed : drawn.card.upright}
                        </p>
                      </>
                    ) : (
                      <p style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                        Tap the card to reveal your {positions[i].label.toLowerCase()}.
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Controls */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap', marginTop: '40px' }}>
              {!allRevealed && count > 1 && (
                <button type="button" onClick={revealAll} className="btn-primary">
                  <Eye size={18} /> Reveal All
                </button>
              )}
              <button type="button" onClick={() => startShuffle(count)} className="btn-secondary">
                <RotateCcw size={18} /> Draw Again
              </button>
              <button type="button" onClick={reset} className="btn-secondary">
                <Shuffle size={18} /> Change Spread
              </button>
            </div>

            {/* Disclaimer + app CTA (shown once all cards are revealed) */}
            {allRevealed && (
              <div
                style={{
                  marginTop: '44px',
                  padding: '28px',
                  borderRadius: '16px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  textAlign: 'center',
                  animation: 'tarotFadeUp 0.6s ease-out',
                }}
              >
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.7, maxWidth: '620px', margin: '0 auto 18px' }}>
                  Tarot offers a mirror for reflection, not a fixed fate. For a personalised forecast rooted in your exact birth chart, nakshatra and dashas, explore the SarvamAstro app.
                </p>
                <Link href="/" className="btn-primary"><Sparkles size={18} /> Get Your Personal Birth Chart</Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/* An empty (or filled) destination slot shown during picking. */
function Slot({ label, filled, index, children }: { label: string; filled: boolean; index: number; children?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: filled ? 'var(--accent)' : 'var(--text-muted)' }}>
        {label}
      </div>
      <div
        style={{
          width: '92px',
          height: '154px',
          borderRadius: '14px',
          border: filled ? '1px solid var(--border-hover)' : '2px dashed var(--border)',
          background: filled ? 'transparent' : 'var(--bg-card)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
          fontSize: '22px',
          fontWeight: 800,
          animation: filled ? 'tarotDealIn 0.5s ease-out' : 'none',
        }}
      >
        {filled ? children : index + 1}
      </div>
    </div>
  )
}
